import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Button as MuiButton, Stack, Typography } from '@mui/material';
import type { MouseEventHandler, ReactNode } from 'react';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { SilobagMassiveRegisterSpreadsheetModal } from '@old/features/spreadsheet/controller/silobag-massive-register';
import { useGlobalStore } from '@old/global-store/use-store';
import { BetaBadge } from '@old/ui/beta-badge';
import * as HOOKS from '@siloreal:shared:hooks';
import { PATH } from '@siloreal:app:config';
import {
  listCountrysidesService,
  type CountrysideListResponse,
} from '@siloreal:shared:api/service/countryside/countryside-list';
import { useRouteController } from '@siloreal:shared:hooks/controller/route/use-route-controller';
import * as UI from '@siloreal:shared:ui';
import { useSilobagListContext } from '../../context/silobag-list-context';
import { HeaderActionsDropdown } from './header-actions-dropdown';

const TAB_BUTTON_BLUE = '#22A7F0';
const TAB_BUTTON_ACTIVE_BACKGROUND = '#EAF8FF';
const TAB_BUTTON_IDLE_BORDER = '#DFE3EA';
const SILOBAG_TEMPLATE_URL =
  'https://iofbucket.s3.us-east-1.amazonaws.com/templates/es/Planilla%2Bimportaci%C3%B3n%2Bde%2BsilobolsasV7.xlsx';

export function SilobagHeader() {
  const {
    state: { current, change },
  } = useSilobagListContext();

  const activeTab = current.view === 'silobag' ? current.tab : null;

  const i18n = HOOKS.useLocale();

  return (
    <UI.Card>
      <Stack direction="row" alignItems="center" gap={4} flexWrap="wrap" px={3} py={2} sx={{ minHeight: 84 }}>
        <Stack alignItems="flex-start" gap={0.25} sx={{ minWidth: 170 }}>
          <Stack direction="column" gap={1} alignItems="center">
            <Typography fontSize={24} fontWeight={900} color="#3A3440" lineHeight={1.05}>
              {i18n.t('temp.common.silobags')}
            </Typography>
            <UI.Button
              variant="alternative-hollow"
              removeBorder
              size="small"
              noPadding
              onClick={() => change.view.silospro()}
              icon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 10 }} />}
              tid="silobag-list-back-to-silospro-btn"
            >
              {i18n.t('temp.private.list.silobag.return_silospro')}
            </UI.Button>
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" gap={1.5}>
          <SilobagTabButton
            active={activeTab === 'active'}
            subtitle={i18n.t('temp.common.silobags')}
            title={i18n.t('temp.private.list.common.actives')}
            icon={<HourglassBottomIcon sx={{ color: '#ffcc00', fontSize: 24 }} />}
            iconBackgroundColor="#FFF6D6"
            onClick={() => change.tab.silobag('active')}
            tid="silobag-list-tab-active"
          />
          <SilobagTabButton
            active={activeTab === 'closed'}
            subtitle={i18n.t('temp.common.silobags')}
            title={i18n.t('temp.common.closed')}
            icon={<LockIcon sx={{ color: '#49454F', fontSize: 24 }} />}
            onClick={() => change.tab.silobag('closed')}
            tid="silobag-list-tab-closed"
          />
          <SilobagTabButton
            active={activeTab === 'archived'}
            subtitle={i18n.t('temp.common.silobags')}
            title={i18n.t('temp.private.list.common.archived')}
            icon={<Inventory2Icon sx={{ color: '#8A8790', fontSize: 24 }} />}
            onClick={() => change.tab.silobag('archived')}
            tid="silobag-list-tab-archived"
          />
        </Stack>

        <Stack direction="row" alignItems="center" gap={1.25} sx={{ marginLeft: 'auto' }}>
          <HeaderActionsDropdown
            view="silobag"
            tid="silobag-list-silobag-actions-btn"
            popupTid="silobag-list-silobag-actions-popup"
          />
          <NewSilobagButton />
        </Stack>
      </Stack>
    </UI.Card>
  );
}

type ActiveProp = { active?: boolean };
type DisplayProp = { title: string; subtitle: string; icon: ReactNode };
type ActionProp = { onClick?: MouseEventHandler<HTMLButtonElement> };
type ExtraProp = { tid?: string; ariaLabel?: string; iconBackgroundColor?: string };
type SilobagTabButtonProps = ActiveProp & DisplayProp & ActionProp & ExtraProp;

function SilobagTabButton(props: SilobagTabButtonProps) {
  const { active = false } = props;
  const { title, subtitle, icon } = props;
  const { onClick } = props;
  const { tid, ariaLabel, iconBackgroundColor = '#F1F1F4' } = props;
  return (
    <MuiButton
      type="button"
      variant="outlined"
      onClick={onClick}
      data-testid={tid}
      aria-label={ariaLabel}
      disableElevation
      sx={{
        minWidth: 188,
        height: 68,
        justifyContent: 'flex-start',
        px: 2,
        py: 1.25,
        borderRadius: '10px',
        borderColor: active ? TAB_BUTTON_BLUE : TAB_BUTTON_IDLE_BORDER,
        borderWidth: active ? 2 : 1,
        backgroundColor: active ? TAB_BUTTON_ACTIVE_BACKGROUND : '#FFFFFF',
        color: '#3A3440',
        textTransform: 'none',
        boxShadow: active ? '0 8px 20px rgba(34, 167, 240, 0.10)' : 'none',
        '&:hover': {
          borderColor: active ? TAB_BUTTON_BLUE : TAB_BUTTON_IDLE_BORDER,
          borderWidth: active ? 2 : 1,
          backgroundColor: active ? TAB_BUTTON_ACTIVE_BACKGROUND : '#FFFFFF',
        },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            backgroundColor: iconBackgroundColor,
            lineHeight: 0,
          }}
        >
          {icon}
        </Box>

        <Stack alignItems="flex-start" lineHeight={1.1}>
          <Typography fontSize={12} color="#7A747F" lineHeight={1.25}>
            {subtitle}
          </Typography>
          <Typography fontSize={18} fontWeight={600} color="#3A3440" lineHeight={1.15}>
            {title}
          </Typography>
        </Stack>
      </Stack>
    </MuiButton>
  );
}

function NewSilobagButton() {
  const { navigate } = useRouteController<Record<string, never>, { isGuest: boolean }>({});
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openMassiveRegisterModal, setOpenMassiveRegisterModal] = useState(false);
  const { organizationId, userOrganizationId, permissions } = useGlobalStore(
    useShallow((state) => ({
      organizationId: state.organizationId,
      userOrganizationId: state.userOrganizationId,
      permissions: state.permissions,
    }))
  );
  const orgWritePermission = permissions?.organization.write ?? false;

  const countrysidesQuery = HOOKS.useSiloRealService.query<
    CountrysideListResponse,
    Error,
    CountrysideListResponse
  >(() => listCountrysidesService({ query: { organization_id: organizationId } }), {
    queryKey: ['countryside-list', 'new-silobag', organizationId],
  });

  const countrysides = countrysidesQuery.data ?? [];
  const isCountrysideLoading = countrysidesQuery.isLoading;
  const isDisabled = isCountrysideLoading || !orgWritePermission;

  const handleRegisterSilobag = () => {
    if (countrysides.length > 0) {
      navigate.visit(PATH.SILOBAG_NEW, {
        state: { isGuest: userOrganizationId !== organizationId },
      });
      return;
    }

    setOpenInfoModal(true);
  };

  const handleDownloadTemplate = () => {
    window.open(SILOBAG_TEMPLATE_URL, '_blank');
  };

  const i18n = HOOKS.useLocale();

  return (
    <>
      <Stack direction="row" alignItems="stretch">
        <MuiButton
          type="button"
          variant="contained"
          startIcon={<AddRoundedIcon fontSize="small" />}
          disableElevation
          disabled={isDisabled}
          onClick={handleRegisterSilobag}
          data-testid="silobag-list-new-silobag-btn"
          sx={{
            height: 38,
            px: 2,
            borderRadius: '9999px 0 0 9999px',
            backgroundColor: '#6BAA00',
            color: '#FFFFFF',
            fontSize: 13,
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#5a9400',
            },
          }}
        >
          {i18n.t('temp.common.new_silobag')}
        </MuiButton>
        <Box
          sx={{
            ml: '-1px',
            '& .MuiButton-root.MuiButton-root': {
              minWidth: 38,
              width: 38,
              height: 38,
              p: 0,
              borderRadius: '0 9999px 9999px 0',
              backgroundColor: '#6BAA00',
              color: '#FFFFFF',
              border: 'none',
              '&:hover': {
                border: 'none',
                backgroundColor: '#5a9400',
              },
              '&.Mui-disabled': {
                border: 'none',
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            },
          }}
        >
          <UI.dropdown.base
            variant="primary"
            icon={<KeyboardArrowDownRoundedIcon fontSize="small" />}
            ariaLabel="Abrir opciones de nuevo silobolsa"
            menuAriaLabel="Opciones de nuevo silobolsa"
            disabled={isDisabled}
            tid="silobag-list-new-silobag-menu-btn"
            popupTid="silobag-list-new-silobag-menu-popup"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <UI.dropdown.option onClick={handleRegisterSilobag}>
              {i18n.t('temp.private.list.silobag.individual_register')}
            </UI.dropdown.option>
            <UI.dropdown.option badge={<BetaBadge />} onClick={() => setOpenMassiveRegisterModal(true)}>
              {i18n.t('temp.private.list.silobag.massive_register')}
            </UI.dropdown.option>
            <UI.dropdown.section />
            <UI.dropdown.option icon="download" onClick={handleDownloadTemplate} badge="excel">
              {i18n.t('temp.private.list.silobag.download_model_spreadsheet')}
            </UI.dropdown.option>
          </UI.dropdown.base>
        </Box>
      </Stack>

      <MissingCountrysideModal open={openInfoModal} onClose={() => setOpenInfoModal(false)} />
      <SilobagMassiveRegisterSpreadsheetModal
        open={openMassiveRegisterModal}
        onClose={() => setOpenMassiveRegisterModal(false)}
      />
    </>
  );
}

function MissingCountrysideModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <UI.Modal
      open={open}
      onClose={onClose}
      title="Alta Silobolsa"
      description="Se tiene que crear un establecimiento antes de crear silobolsas. Ver dentro de pestaña Administrados > Establecimientos > +Nuevo Establecimiento."
      actions={
        <UI.Button
          variant="text-link"
          uppercase={false}
          onClick={onClose}
          tid="silobag-list-missing-countryside-modal-close-btn"
        >
          Cerrar
        </UI.Button>
      }
      tid="silobag-list-missing-countryside-modal"
    />
  );
}
